/**
 * Rede de segurança: o pedaço do build que sumiu do servidor.
 *
 * Os assets levam hash no nome e são servidos como `immutable`
 * (`apps/web/nginx.conf`). Depois de um deploy, os arquivos da versão anterior
 * **não existem mais** — quem está com a aba aberta e navega para uma tela
 * carregada sob demanda recebe `Failed to fetch dynamically imported module` e
 * fica com tela branca. Não é cosmético: é a tela não abrir.
 *
 * A verificação de versão cobre o caso normal. Isto cobre a corrida: o usuário
 * clicou no menu no mesmo minuto do deploy, antes de qualquer checagem.
 * Recarregar resolve, porque o `index.html` é `no-cache` e traz os nomes novos.
 *
 * ⚠️ Recarrega NO MÁXIMO uma vez por sessão. Se o erro não for deploy — um chunk
 * realmente quebrado, rede caindo —, um segundo recarregamento entraria em laço
 * infinito, que é muito pior que a tela branca: a pessoa não consegue nem ler a
 * mensagem de erro para pedir ajuda.
 */

const GUARD_KEY = 'app-version:chunk-reloaded'

/** O erro é de módulo que não carregou (e não um erro de dentro do módulo)? */
function isChunkLoadError(reason: unknown): boolean {
    const message =
        reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : ''
    return (
        /Failed to fetch dynamically imported module/i.test(message) ||
        /error loading dynamically imported module/i.test(message) ||
        /Importing a module script failed/i.test(message) ||
        // Safari
        /Unable to preload/i.test(message)
    )
}

function reloadOnce(): void {
    try {
        if (sessionStorage.getItem(GUARD_KEY) === '1') return
        sessionStorage.setItem(GUARD_KEY, '1')
    } catch {
        // Sem armazenamento não há trava, e sem trava não se recarrega sozinho.
        return
    }
    window.location.reload()
}

/** Liga a rede de segurança. Chamar uma vez, na subida do app. */
export function installChunkReload(): void {
    // Evento do próprio Vite para o preload de chunk que falhou.
    window.addEventListener('vite:preloadError', event => {
        event.preventDefault()
        reloadOnce()
    })

    // O `import()` que falha dentro do React Router vira rejeição não tratada.
    window.addEventListener('unhandledrejection', event => {
        if (isChunkLoadError(event.reason)) reloadOnce()
    })
}
